import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  FileEdit,
  Plus,
  Search,
  Edit3,
  Archive,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  AlertTriangle,
  Tag,
  Globe,
  Building,
  ChevronDown,
  Shield,
  Copy,
  ToggleLeft,
  ToggleRight,
  Loader2,
  Star,
  FileText,
  Check,
  Scale,
  BookOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Enums matching backend models
const LETTER_FAMILIES = [
  { value: 'factual', label: 'Factual', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { value: 'metro2', label: 'Metro2', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { value: 'structural', label: 'Structural', color: 'bg-teal-500/10 text-teal-400 border-teal-500/30' },
  { value: 'inquiry', label: 'Inquiry', color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  { value: 'collection', label: 'Collection', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
  { value: 'data_breach', label: 'Data Breach', color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  { value: 'personal_info', label: 'Personal Info', color: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
  { value: 'identity_theft', label: 'Identity Theft', color: 'bg-rose-500/10 text-rose-400 border-rose-500/30' },
  { value: 'goodwill', label: 'Goodwill', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' },
  { value: 'bankruptcy', label: 'Bankruptcy', color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' },
  { value: 'generic', label: 'Generic', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30' },
];

const DISPUTE_METHODS = [
  { value: 'factual_inaccuracy', label: 'Factual Inaccuracy' },
  { value: 'metro2_technical', label: 'Metro2 Technical' },
  { value: 'data_breach_method', label: 'Data Breach' },
  { value: 'personal_info_accuracy', label: 'Personal Info Accuracy' },
  { value: 'debt_validation', label: 'Debt Validation' },
  { value: 'unauthorized_inquiry', label: 'Unauthorized Inquiry' },
  { value: 'structural_method', label: 'Structural' },
  { value: 'recovery_method', label: 'Recovery' },
  { value: 'isolated_method', label: 'Isolated' },
  { value: 'identity_theft', label: 'Identity Theft' },
  { value: 'goodwill_adjustment', label: 'Goodwill Adjustment' },
];

const TRAINING_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  { value: 'active', label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  { value: 'inactive', label: 'Inactive', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: XCircle },
  { value: 'archived', label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
];

const SCOPES = [
  { value: 'global', label: 'Global', icon: Globe },
  { value: 'tenant', label: 'Tenant', icon: Building },
];

const BUREAU_TARGETS = [
  { value: 'all', label: 'All Bureaus' },
  { value: 'transunion', label: 'TransUnion' },
  { value: 'experian', label: 'Experian' },
  { value: 'equifax', label: 'Equifax' },
];

const ITEM_CATEGORIES = [
  { value: 'account', label: 'Account' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'personal_info', label: 'Personal Info' },
  { value: 'collection', label: 'Collection' },
  { value: 'chargeoff', label: 'Charge-off' },
  { value: 'late_payment', label: 'Late Payment' },
  { value: 'public_record', label: 'Public Record' },
  { value: 'bankruptcy', label: 'Bankruptcy' },
  { value: 'any', label: 'Any' },
];

const QUALITY_RATINGS = [
  { value: 'excellent', label: 'Excellent', stars: 5, color: 'text-emerald-400' },
  { value: 'strong', label: 'Strong', stars: 4, color: 'text-amber-400' },
  { value: 'average', label: 'Average', stars: 3, color: 'text-slate-400' },
  { value: 'weak', label: 'Weak', stars: 2, color: 'text-red-400' },
];

const DEFAULT_FORM = {
  scope: 'global',
  tenant_id: '',
  letter_family: 'factual',
  dispute_method: 'factual_inaccuracy',
  item_category: 'any',
  bureau_target: 'all',
  account_category: 'any',
  title: '',
  description: '',
  scenario_tags: [],
  sample_input_context: null,
  example_letter: '',
  section_map: [],
  compliance_notes: '',
  legal_citations: [],
  required_sections: [],
  quality_rating: 'average',
  priority: 50,
  strength_factors: [],
  weakness_factors: [],
  status: 'draft',
};

const LetterTrainingManagement = () => {
  const { user, getAuthHeaders } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    letter_family: '',
    dispute_method: '',
    scope: '',
    is_approved: '',
    search: '',
  });
  
  // Pagination
  const [pagination, setPagination] = useState({ skip: 0, limit: 25, total: 0 });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [previewingLetter, setPreviewingLetter] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [citationsInput, setCitationsInput] = useState('');
  const [strengthsInput, setStrengthsInput] = useState('');
  const [weaknessesInput, setWeaknessesInput] = useState('');
  
  // Fetch records
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.letter_family) params.append('letter_family', filters.letter_family);
      if (filters.dispute_method) params.append('dispute_method', filters.dispute_method);
      if (filters.scope) params.append('scope', filters.scope);
      if (filters.is_approved === 'true') params.append('is_approved', 'true');
      if (filters.is_approved === 'false') params.append('is_approved', 'false');
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());
      
      const response = await axios.get(`${API}/letter-training/records?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      let fetchedRecords = response.data.records || [];
      
      // Apply local search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        fetchedRecords = fetchedRecords.filter(r =>
          r.title?.toLowerCase().includes(search) ||
          r.description?.toLowerCase().includes(search) ||
          r.example_letter?.toLowerCase().includes(search) ||
          r.scenario_tags?.some(t => t.toLowerCase().includes(search))
        );
      }
      
      setRecords(fetchedRecords);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch records:', error);
      toast.error('Failed to load letter training records');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit, getAuthHeaders]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/letter-training/stats`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [getAuthHeaders]);
  
  useEffect(() => {
    fetchRecords();
    fetchStats();
  }, [fetchRecords, fetchStats]);
  
  // Handle create
  const handleCreate = async () => {
    if (!formData.title.trim()) {
      setFormErrors({ title: 'Title is required' });
      return;
    }
    if (!formData.example_letter.trim()) {
      setFormErrors({ example_letter: 'Example letter content is required' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        scenario_tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        legal_citations: citationsInput ? citationsInput.split(',').map(c => c.trim()).filter(Boolean) : [],
        strength_factors: strengthsInput ? strengthsInput.split(',').map(s => s.trim()).filter(Boolean) : [],
        weakness_factors: weaknessesInput ? weaknessesInput.split(',').map(w => w.trim()).filter(Boolean) : [],
      };
      
      await axios.post(`${API}/letter-training/records`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      toast.success('Letter training record created');
      setShowCreateDialog(false);
      resetForm();
      fetchRecords();
      fetchStats();
    } catch (error) {
      console.error('Failed to create record:', error);
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle update
  const handleUpdate = async () => {
    if (!editingRecord || !formData.title.trim()) {
      setFormErrors({ title: 'Title is required' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        scenario_tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        legal_citations: citationsInput ? citationsInput.split(',').map(c => c.trim()).filter(Boolean) : [],
        strength_factors: strengthsInput ? strengthsInput.split(',').map(s => s.trim()).filter(Boolean) : [],
        weakness_factors: weaknessesInput ? weaknessesInput.split(',').map(w => w.trim()).filter(Boolean) : [],
      };
      
      await axios.put(`${API}/letter-training/records/${editingRecord.id}`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      toast.success('Record updated');
      setEditingRecord(null);
      resetForm();
      fetchRecords();
      fetchStats();
    } catch (error) {
      console.error('Failed to update record:', error);
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle approve
  const handleApprove = async (record) => {
    try {
      await axios.post(`${API}/letter-training/records/${record.id}/approve`, {
        approved_by: user?.email || user?.name || 'admin'
      }, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Record approved - now eligible for live generation');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error('Failed to approve record');
    }
  };
  
  // Handle status toggle (activate/deactivate)
  const handleStatusToggle = async (record) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/letter-training/records/${record.id}`, { status: newStatus }, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success(`Record ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Handle archive (soft delete)
  const handleArchive = async (record) => {
    try {
      await axios.delete(`${API}/letter-training/records/${record.id}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Record archived');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error('Failed to archive record');
    }
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({ ...DEFAULT_FORM });
    setTagsInput('');
    setCitationsInput('');
    setStrengthsInput('');
    setWeaknessesInput('');
    setFormErrors({});
  };
  
  // Open edit dialog
  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      scope: record.scope || 'global',
      tenant_id: record.tenant_id || '',
      letter_family: record.letter_family || 'factual',
      dispute_method: record.dispute_method || 'factual_inaccuracy',
      item_category: record.item_category || 'any',
      bureau_target: record.bureau_target || 'all',
      account_category: record.account_category || 'any',
      title: record.title || '',
      description: record.description || '',
      sample_input_context: record.sample_input_context || null,
      example_letter: record.example_letter || '',
      section_map: record.section_map || [],
      compliance_notes: record.compliance_notes || '',
      required_sections: record.required_sections || [],
      quality_rating: record.quality_rating || 'average',
      priority: record.priority ?? 50,
      status: record.status || 'draft',
    });
    setTagsInput((record.scenario_tags || []).join(', '));
    setCitationsInput((record.legal_citations || []).join(', '));
    setStrengthsInput((record.strength_factors || []).join(', '));
    setWeaknessesInput((record.weakness_factors || []).join(', '));
    setFormErrors({});
  };
  
  // Get info helpers
  const getStatusInfo = (status) => TRAINING_STATUSES.find(s => s.value === status) || TRAINING_STATUSES[0];
  const getFamilyInfo = (family) => LETTER_FAMILIES.find(f => f.value === family) || LETTER_FAMILIES[0];
  const getMethodInfo = (method) => DISPUTE_METHODS.find(m => m.value === method) || DISPUTE_METHODS[0];
  const getScopeInfo = (scope) => SCOPES.find(s => s.value === scope) || SCOPES[0];
  const getQualityInfo = (rating) => QUALITY_RATINGS.find(q => q.value === rating) || QUALITY_RATINGS[2];
  
  // Render quality stars
  const renderQualityStars = (rating) => {
    const info = getQualityInfo(rating);
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            size={12}
            className={i < info.stars ? `${info.color} fill-current` : 'text-slate-600'}
          />
        ))}
      </div>
    );
  };
  
  // Check admin access
  const ADMIN_ROLES = ['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'];
  if (!ADMIN_ROLES.includes(user?.role)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Admin access required.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="letter-training-management-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileEdit className="text-amber-500" size={28} />
            Letter Training
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage few-shot letter examples for dispute letter generation
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => { fetchRecords(); fetchStats(); }} data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { setShowCreateDialog(true); resetForm(); }} data-testid="create-record-btn">
            <Plus size={16} className="mr-2" />
            New Example
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total_records || 0}</p>
            <p className="text-xs text-slate-400">Total Examples</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.by_status?.active || 0}</p>
            <p className="text-xs text-emerald-400/70">Active</p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{stats.approved_count || 0}</p>
            <p className="text-xs text-amber-400/70">Approved</p>
          </div>
          <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-slate-400">{stats.by_status?.draft || 0}</p>
            <p className="text-xs text-slate-400/70">Draft</p>
          </div>
          <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-teal-400">{stats.by_status?.inactive || 0}</p>
            <p className="text-xs text-teal-400/70">Inactive</p>
          </div>
          <div className="bg-stone-700/50 border border-slate-600 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-slate-500">{stats.by_status?.archived || 0}</p>
            <p className="text-xs text-slate-500/70">Archived</p>
          </div>
        </div>
      )}
      
      {/* Production Notice */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-400 flex-shrink-0 mt-0.5" size={18} />
          <div>
            <p className="text-sm text-amber-400 font-medium">Production Influence</p>
            <p className="text-xs text-amber-400/70 mt-1">
              Only <strong>Approved + Active</strong> letter examples are used by the live few-shot generator. 
              Draft and Inactive examples are for testing/review only.
            </p>
          </div>
        </div>
      </div>
      
      {/* Filters */}
      <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search examples..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 bg-stone-900 border-slate-700 text-white"
                data-testid="search-input"
              />
            </div>
          </div>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] bg-stone-900 border-slate-700 text-white" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Statuses</SelectItem>
              {TRAINING_STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.letter_family || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, letter_family: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[150px] bg-stone-900 border-slate-700 text-white" data-testid="filter-family">
              <SelectValue placeholder="Family" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Families</SelectItem>
              {LETTER_FAMILIES.map(f => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.dispute_method || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, dispute_method: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] bg-stone-900 border-slate-700 text-white" data-testid="filter-method">
              <SelectValue placeholder="Method" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Methods</SelectItem>
              {DISPUTE_METHODS.map(m => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.is_approved || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, is_approved: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] bg-stone-900 border-slate-700 text-white" data-testid="filter-approved">
              <SelectValue placeholder="Approval" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="true">Approved</SelectItem>
              <SelectItem value="false">Not Approved</SelectItem>
            </SelectContent>
          </Select>
          {(filters.status || filters.letter_family || filters.dispute_method || filters.is_approved || filters.search) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ status: '', letter_family: '', dispute_method: '', scope: '', is_approved: '', search: '' })}
              className="text-slate-400 hover:text-white"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
      
      {/* Records List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-32 bg-stone-800" />)}
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-16 bg-stone-800/30 border border-slate-700 rounded-xl">
          <FileEdit size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Letter Training Examples</h3>
          <p className="text-slate-400 mb-4">Create your first example to improve letter generation quality.</p>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-btn">
            <Plus size={16} className="mr-2" />
            Create Example
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((record) => {
            const statusInfo = getStatusInfo(record.status);
            const familyInfo = getFamilyInfo(record.letter_family);
            const methodInfo = getMethodInfo(record.dispute_method);
            const scopeInfo = getScopeInfo(record.scope);
            const StatusIcon = statusInfo.icon;
            const ScopeIcon = scopeInfo.icon;
            const isActive = record.status === 'active';
            const isApproved = record.is_approved;
            const isProductionReady = isActive && isApproved;
            
            return (
              <div
                key={record.id}
                className={`bg-stone-800/50 border rounded-xl p-4 transition-all hover:border-amber-500/30 ${
                  isProductionReady ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700'
                }`}
                data-testid={`record-${record.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-white truncate">{record.title}</h3>
                      {isProductionReady && (
                        <span className="px-2 py-0.5 text-xs rounded border bg-emerald-500/20 text-emerald-400 border-emerald-500/30 flex items-center gap-1">
                          <CheckCircle size={10} />
                          Production Ready
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded border ${statusInfo.color}`}>
                        <StatusIcon size={12} className="inline mr-1" />
                        {statusInfo.label}
                      </span>
                      {isApproved && !isProductionReady && (
                        <span className="px-2 py-0.5 text-xs rounded border bg-amber-500/20 text-amber-400 border-amber-500/30">
                          Approved
                        </span>
                      )}
                      <span className={`px-2 py-0.5 text-xs rounded border ${familyInfo.color}`}>
                        {familyInfo.label}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded border border-slate-600 text-slate-400">
                        {methodInfo.label}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded border border-slate-600 text-slate-400">
                        <ScopeIcon size={12} className="inline mr-1" />
                        {scopeInfo.label}
                      </span>
                      {renderQualityStars(record.quality_rating)}
                    </div>
                    
                    {record.description && (
                      <p className="text-sm text-slate-400 mb-2 line-clamp-1">{record.description}</p>
                    )}
                    
                    {/* Letter preview snippet */}
                    <div className="bg-stone-900/50 rounded-lg p-2 mb-2">
                      <p className="text-xs text-slate-500 font-mono line-clamp-2">
                        {record.example_letter?.substring(0, 200)}...
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span>Bureau: {record.bureau_target}</span>
                      <span>Item: {record.item_category}</span>
                      <span>Priority: {record.priority}</span>
                      {record.legal_citations?.length > 0 && (
                        <span className="text-teal-400">
                          <Scale size={10} className="inline mr-1" />
                          {record.legal_citations.length} citations
                        </span>
                      )}
                      {record.scenario_tags?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Tag size={10} />
                          {record.scenario_tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">{tag}</span>
                          ))}
                          {record.scenario_tags.length > 3 && <span>+{record.scenario_tags.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Approve button */}
                    {!record.is_approved && record.status !== 'archived' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleApprove(record)}
                        className="text-amber-400 hover:bg-amber-500/10"
                        title="Approve for production"
                        data-testid={`approve-${record.id}`}
                      >
                        <Check size={16} />
                      </Button>
                    )}
                    
                    {/* Active toggle */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStatusToggle(record)}
                      className={isActive ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-slate-400 hover:bg-slate-700'}
                      title={isActive ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-${record.id}`}
                    >
                      {isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </Button>
                    
                    {/* Preview Letter */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPreviewingLetter(record)}
                      className="text-teal-400 hover:bg-teal-500/10"
                      title="Preview full letter"
                      data-testid={`preview-${record.id}`}
                    >
                      <BookOpen size={16} />
                    </Button>
                    
                    {/* View */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingRecord(record)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`view-${record.id}`}
                    >
                      <Eye size={16} />
                    </Button>
                    
                    {/* Edit */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(record)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`edit-${record.id}`}
                    >
                      <Edit3 size={16} />
                    </Button>
                    
                    {/* More actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-slate-400">
                          <ChevronDown size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-stone-800 border-slate-700">
                        <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(record.id); toast.success('ID copied'); }}>
                          <Copy size={14} className="mr-2" />
                          Copy ID
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-700" />
                        <DropdownMenuItem
                          onClick={() => handleArchive(record)}
                          className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                        >
                          <Archive size={14} className="mr-2" />
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip === 0}
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip + pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || !!editingRecord} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); setEditingRecord(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRecord ? 'Edit Letter Training Example' : 'Create Letter Training Example'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingRecord ? 'Update the letter example fields below.' : 'Add a new approved letter example for few-shot generation.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-white">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Factual Inaccuracy - Collection Account Dispute"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-title"
                />
                {formErrors.title && <p className="text-xs text-red-400">{formErrors.title}</p>}
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Letter Family</Label>
                <Select value={formData.letter_family} onValueChange={(v) => setFormData(prev => ({ ...prev, letter_family: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-family">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {LETTER_FAMILIES.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Dispute Method</Label>
                <Select value={formData.dispute_method} onValueChange={(v) => setFormData(prev => ({ ...prev, dispute_method: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {DISPUTE_METHODS.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Scope & Status */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Scope</Label>
                <Select value={formData.scope} onValueChange={(v) => setFormData(prev => ({ ...prev, scope: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {SCOPES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex items-center gap-2">
                          <s.icon size={14} />
                          {s.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {TRAINING_STATUSES.filter(s => s.value !== 'archived').map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Quality Rating</Label>
                <Select value={formData.quality_rating} onValueChange={(v) => setFormData(prev => ({ ...prev, quality_rating: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-quality">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {QUALITY_RATINGS.map(q => (
                      <SelectItem key={q.value} value={q.value}>
                        <div className="flex items-center gap-2">
                          {q.label}
                          <span className={q.color}>({q.stars} stars)</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Classification */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Item Category</Label>
                <Select value={formData.item_category} onValueChange={(v) => setFormData(prev => ({ ...prev, item_category: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-item-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {ITEM_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Bureau Target</Label>
                <Select value={formData.bureau_target} onValueChange={(v) => setFormData(prev => ({ ...prev, bureau_target: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-bureau">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {BUREAU_TARGETS.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Priority (0-100)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 50 }))}
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-priority"
                />
              </div>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of when this example should be used..."
                className="bg-stone-800 border-slate-700 text-white min-h-[60px]"
                data-testid="form-description"
              />
            </div>
            
            {/* Example Letter */}
            <div className="space-y-2">
              <Label className="text-white">Example Letter Content *</Label>
              <Textarea
                value={formData.example_letter}
                onChange={(e) => setFormData(prev => ({ ...prev, example_letter: e.target.value }))}
                placeholder="Paste the full approved letter text here..."
                className="bg-stone-800 border-slate-700 text-white min-h-[200px] font-mono text-sm"
                data-testid="form-letter"
              />
              {formErrors.example_letter && <p className="text-xs text-red-400">{formErrors.example_letter}</p>}
              <p className="text-xs text-slate-500">
                Use placeholders like [CONSUMER_NAME], [ACCOUNT_NUMBER], [CREDITOR_NAME] for variable data.
              </p>
            </div>
            
            {/* Compliance Notes */}
            <div className="space-y-2">
              <Label className="text-white">Compliance Notes</Label>
              <Textarea
                value={formData.compliance_notes}
                onChange={(e) => setFormData(prev => ({ ...prev, compliance_notes: e.target.value }))}
                placeholder="Any compliance considerations or legal notes..."
                className="bg-stone-800 border-slate-700 text-white min-h-[60px]"
                data-testid="form-compliance"
              />
            </div>
            
            {/* Tags and Citations */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Scenario Tags (comma-separated)</Label>
                <Input
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                  placeholder="e.g., collection, medical_debt, cfpb"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-tags"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Legal Citations (comma-separated)</Label>
                <Input
                  value={citationsInput}
                  onChange={(e) => setCitationsInput(e.target.value)}
                  placeholder="e.g., FCRA 611, FDCPA 809(b)"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-citations"
                />
              </div>
            </div>
            
            {/* Strength/Weakness */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Strength Factors (comma-separated)</Label>
                <Input
                  value={strengthsInput}
                  onChange={(e) => setStrengthsInput(e.target.value)}
                  placeholder="e.g., strong_legal_basis, specific_citations"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-strengths"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Weakness Factors (comma-separated)</Label>
                <Input
                  value={weaknessesInput}
                  onChange={(e) => setWeaknessesInput(e.target.value)}
                  placeholder="e.g., lengthy, complex_language"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-weaknesses"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setEditingRecord(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={editingRecord ? handleUpdate : handleCreate} disabled={saving} data-testid="form-submit">
              {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
              {editingRecord ? 'Update Example' : 'Create Example'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Details Dialog */}
      <Dialog open={!!viewingRecord} onOpenChange={(open) => { if (!open) setViewingRecord(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye size={18} />
              {viewingRecord?.title}
            </DialogTitle>
          </DialogHeader>
          
          {viewingRecord && (
            <div className="space-y-4 py-4">
              <div className="flex flex-wrap gap-2">
                {viewingRecord.is_approved && viewingRecord.status === 'active' && (
                  <span className="px-2 py-1 text-xs rounded border bg-emerald-500/20 text-emerald-400 border-emerald-500/30 flex items-center gap-1">
                    <CheckCircle size={10} />
                    Production Ready
                  </span>
                )}
                <span className={`px-2 py-1 text-xs rounded border ${getStatusInfo(viewingRecord.status).color}`}>
                  {getStatusInfo(viewingRecord.status).label}
                </span>
                {viewingRecord.is_approved && (
                  <span className="px-2 py-1 text-xs rounded border bg-amber-500/20 text-amber-400 border-amber-500/30">
                    Approved
                  </span>
                )}
                <span className={`px-2 py-1 text-xs rounded border ${getFamilyInfo(viewingRecord.letter_family).color}`}>
                  {getFamilyInfo(viewingRecord.letter_family).label}
                </span>
                <span className="px-2 py-1 text-xs rounded border border-slate-600 text-slate-400">
                  {getMethodInfo(viewingRecord.dispute_method).label}
                </span>
                {renderQualityStars(viewingRecord.quality_rating)}
              </div>
              
              {viewingRecord.description && (
                <div>
                  <Label className="text-slate-400 text-xs">Description</Label>
                  <p className="text-sm text-white mt-1">{viewingRecord.description}</p>
                </div>
              )}
              
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <Label className="text-slate-400 text-xs">Scope</Label>
                  <p className="text-white">{viewingRecord.scope}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Bureau</Label>
                  <p className="text-white">{viewingRecord.bureau_target}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Item Category</Label>
                  <p className="text-white">{viewingRecord.item_category}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Priority</Label>
                  <p className="text-white">{viewingRecord.priority}</p>
                </div>
              </div>
              
              {viewingRecord.legal_citations?.length > 0 && (
                <div>
                  <Label className="text-slate-400 text-xs">Legal Citations</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewingRecord.legal_citations.map((c, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-teal-500/20 text-teal-400 rounded">{c}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingRecord.compliance_notes && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <Label className="text-amber-400 text-xs">Compliance Notes</Label>
                  <p className="text-sm text-amber-300 mt-1">{viewingRecord.compliance_notes}</p>
                </div>
              )}
              
              {viewingRecord.scenario_tags?.length > 0 && (
                <div>
                  <Label className="text-slate-400 text-xs">Scenario Tags</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewingRecord.scenario_tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-xs text-slate-500 pt-4 border-t border-slate-700">
                <div>
                  <span>Created: {new Date(viewingRecord.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <span>Updated: {new Date(viewingRecord.updated_at).toLocaleString()}</span>
                </div>
                <div>
                  <span>ID: {viewingRecord.id}</span>
                </div>
                {viewingRecord.approved_by && (
                  <div>
                    <span>Approved by: {viewingRecord.approved_by}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingRecord(null)}>Close</Button>
            <Button variant="outline" onClick={() => { setPreviewingLetter(viewingRecord); setViewingRecord(null); }}>
              <BookOpen size={14} className="mr-2" />
              Preview Letter
            </Button>
            <Button onClick={() => { openEditDialog(viewingRecord); setViewingRecord(null); }}>
              <Edit3 size={14} className="mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Letter Preview Dialog */}
      <Dialog open={!!previewingLetter} onOpenChange={(open) => { if (!open) setPreviewingLetter(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <BookOpen size={18} className="text-teal-400" />
              Letter Preview: {previewingLetter?.title}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Full approved letter example content
            </DialogDescription>
          </DialogHeader>
          
          {previewingLetter && (
            <div className="py-4">
              <div className="flex items-center gap-2 mb-4">
                {previewingLetter.is_approved && previewingLetter.status === 'active' && (
                  <span className="px-2 py-1 text-xs rounded border bg-emerald-500/20 text-emerald-400 border-emerald-500/30 flex items-center gap-1">
                    <CheckCircle size={10} />
                    Production Ready
                  </span>
                )}
                <span className={`px-2 py-1 text-xs rounded border ${getFamilyInfo(previewingLetter.letter_family).color}`}>
                  {getFamilyInfo(previewingLetter.letter_family).label}
                </span>
                <span className="px-2 py-1 text-xs rounded border border-slate-600 text-slate-400">
                  {getMethodInfo(previewingLetter.dispute_method).label}
                </span>
                {renderQualityStars(previewingLetter.quality_rating)}
              </div>
              
              {/* Letter content */}
              <div className="bg-white text-slate-900 rounded-lg p-6 shadow-lg max-h-[60vh] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-serif text-sm leading-relaxed">
                  {previewingLetter.example_letter}
                </pre>
              </div>
              
              {/* Metadata below */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="grid grid-cols-3 gap-4 text-xs">
                  {previewingLetter.legal_citations?.length > 0 && (
                    <div>
                      <Label className="text-slate-400 text-xs">Legal Citations</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {previewingLetter.legal_citations.map((c, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-teal-500/20 text-teal-400 rounded text-[10px]">{c}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {previewingLetter.strength_factors?.length > 0 && (
                    <div>
                      <Label className="text-slate-400 text-xs">Strengths</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {previewingLetter.strength_factors.map((s, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 rounded text-[10px]">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {previewingLetter.weakness_factors?.length > 0 && (
                    <div>
                      <Label className="text-slate-400 text-xs">Weaknesses</Label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {previewingLetter.weakness_factors.map((w, i) => (
                          <span key={i} className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px]">{w}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(previewingLetter?.example_letter || '');
                toast.success('Letter content copied to clipboard');
              }}
            >
              <Copy size={14} className="mr-2" />
              Copy Letter
            </Button>
            <Button variant="outline" onClick={() => setPreviewingLetter(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LetterTrainingManagement;
